'use client';

import { c as _c } from "react/compiler-runtime";
import { SubheadingH3 } from '@/components/ui/typography/subheading-h3';
import { changeUserText, loggedInAsText, loginWithCeviDatabaseText } from '@/features/payload-cms/components/form/static-form-texts';
import { getFormStorageKey } from '@/features/payload-cms/components/form/utils/get-form-storage-key';
import { useReleasePushSubscriptions } from '@/hooks/use-release-push-subscriptions';
import { flushPersonalData } from '@/lib/flush-personal-data';
import { PERSISTER_THROTTLE_MS } from '@/trpc/query-persistence';
import type { StaticTranslationString } from '@/types/types';
import { i18nConfig, type Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { useQueryClient } from '@tanstack/react-query';
import { signIn, signOut, useSession } from 'next-auth/react';
import { useCurrentLocale } from 'next-i18n-router/client';
import React, { useEffect } from 'react';
import type { FieldError, FieldErrorsImpl, FieldValues, Merge } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
interface CeviDatabaseLoginProperties {
  name: string;
  label?: string;
  saveField?: 'name' | 'uuid' | 'email' | 'nickname';
  fieldMapping?: {
    jwtField: string;
    formField: string;
  }[];
  formId?: string;
  required?: boolean;
  currentStepIndex?: number;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<FieldValues>>;
}
const loginRequiredMessage: StaticTranslationString = {
  en: 'You must log in to proceed',
  de: 'Sie müssen sich anmelden, um fortzufahren',
  fr: 'Vous devez vous connecter pour continuer'
};
export const CeviDatabaseLogin: React.FC<CeviDatabaseLoginProperties> = t0 => {
  const $ = _c(49);
  const {
    name,
    label,
    required,
    saveField,
    fieldMapping,
    formId,
    currentStepIndex,
    error
  } = t0;
  const {
    register,
    setValue,
    getValues
  } = useFormContext();
  const {
    data: session
  } = useSession();
  const currentLocale = useCurrentLocale(i18nConfig);
  const locale = (currentLocale ?? "en") as Locale;
  const releasePushSubscriptions = useReleasePushSubscriptions();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== currentStepIndex || $[1] !== formId || $[2] !== getValues) {
    t1 = () => {
      const values = getValues();
      if (typeof formId === "string" && formId !== "") {
        sessionStorage.setItem(getFormStorageKey(formId, "state"), JSON.stringify(values));
        if (currentStepIndex !== undefined) {
          sessionStorage.setItem(getFormStorageKey(formId, "step"), String(currentStepIndex));
        }
      }
      if (typeof globalThis !== "undefined" && globalThis.history) {
        globalThis.history.replaceState(undefined, "", globalThis.location.href);
      }
      const callbackUrl = typeof globalThis === "undefined" ? undefined : globalThis.location.href;
      const signInOptions = {};
      if (typeof callbackUrl === "string" && callbackUrl !== "") {
        signInOptions.callbackUrl = callbackUrl;
      }
      signIn("cevi-db", signInOptions);
    };
    $[0] = currentStepIndex;
    $[1] = formId;
    $[2] = getValues;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleLogin = t1;
  let t2;
  if ($[4] !== currentStepIndex || $[5] !== formId || $[6] !== getValues || $[7] !== queryClient || $[8] !== releasePushSubscriptions) {
    t2 = () => {
      const values_0 = getValues();
      if (typeof formId === "string" && formId !== "") {
        sessionStorage.setItem(getFormStorageKey(formId, "state"), JSON.stringify(values_0));
        if (currentStepIndex !== undefined) {
          sessionStorage.setItem(getFormStorageKey(formId, "step"), String(currentStepIndex));
        }
      }
      const callbackUrl_0 = typeof globalThis === "undefined" ? undefined : globalThis.location.href;
      const signInOptions_0 = {};
      if (typeof callbackUrl_0 === "string" && callbackUrl_0 !== "") {
        signInOptions_0.callbackUrl = callbackUrl_0;
      }
      releasePushSubscriptions().then(_temp).then(async () => {
        queryClient.clear();
        await new Promise(_temp2);
        flushPersonalData({
          clearCachedPages: true
        });
        signIn("cevi-db", signInOptions_0);
      }).catch(_temp3);
    };
    $[4] = currentStepIndex;
    $[5] = formId;
    $[6] = getValues;
    $[7] = queryClient;
    $[8] = releasePushSubscriptions;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleChangeUser = t2;
  let t3;
  if ($[10] !== fieldMapping) {
    t3 = fieldMapping ? JSON.stringify(fieldMapping) : undefined;
    $[10] = fieldMapping;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const fieldMappingString = t3;
  let t4;
  let t5;
  if ($[12] !== currentStepIndex || $[13] !== fieldMappingString || $[14] !== formId || $[15] !== getValues || $[16] !== name || $[17] !== saveField || $[18] !== session || $[19] !== setValue) {
    t4 = () => {
      if (session?.user) {
        let valueToSave;
        bb62: switch (saveField) {
          case "uuid":
            {
              valueToSave = session.user.cevi_db_uuid ?? session.user.uuid;
              break bb62;
            }
          case "name":
            {
              valueToSave = session.user.name;
              break bb62;
            }
          case "nickname":
            {
              valueToSave = session.user.nickname;
              break bb62;
            }
          default:
            {
              valueToSave = session.user.email;
            }
        }
        setValue(name, valueToSave ?? "", {
          shouldValidate: true
        });
        const parsedFieldMapping = fieldMappingString ? JSON.parse(fieldMappingString) as {
          jwtField: string;
          formField: string;
        }[] : undefined;
        if (parsedFieldMapping && parsedFieldMapping.length > 0) {
          let didPrefill = false;
          for (const {
            jwtField,
            formField
          } of parsedFieldMapping) {
            let jwtValue;
            bb89: switch (jwtField) {
              case "name":
                {
                  jwtValue = session.user.name;
                  break bb89;
                }
              case "firstName":
                {
                  jwtValue = session.user.firstName;
                  break bb89;
                }
              case "lastName":
                {
                  jwtValue = session.user.lastName;
                  break bb89;
                }
              case "email":
                {
                  jwtValue = session.user.email;
                  break bb89;
                }
              case "nickname":
                {
                  jwtValue = session.user.nickname;
                  break bb89;
                }
              case "uuid":
                {
                  jwtValue = session.user.uuid;
                  break bb89;
                }
              case "cevi_db_uuid":
                {
                  jwtValue = session.user.cevi_db_uuid;
                  break bb89;
                }
              default:
                {}
            }
            const jwtValueString = jwtValue !== undefined && jwtValue !== null ? String(jwtValue).trim() : "";
            if (jwtValueString.length > 0) {
              const currentValue = getValues(formField) as unknown;
              let currentValueString = "";
              if (typeof currentValue === "string" || typeof currentValue === "number") {
                currentValueString = String(currentValue).trim();
              } else {
                if (currentValue !== undefined && currentValue !== null) {
                  currentValueString = "has_value";
                }
              }
              if (currentValueString.length === 0) {
                setValue(formField, String(jwtValue), {
                  shouldValidate: true
                });
                didPrefill = true;
              }
            }
          }
          if (didPrefill && typeof formId === "string" && currentStepIndex !== undefined) {
            sessionStorage.setItem(getFormStorageKey(formId, "prefill"), String(currentStepIndex + 1));
          }
        }
      }
    };
    t5 = [session, saveField, setValue, name, fieldMappingString, getValues, formId, currentStepIndex];
    $[12] = currentStepIndex;
    $[13] = fieldMappingString;
    $[14] = formId;
    $[15] = getValues;
    $[16] = name;
    $[17] = saveField;
    $[18] = session;
    $[19] = setValue;
    $[20] = t4;
    $[21] = t5;
  } else {
    t4 = $[20];
    t5 = $[21];
  }
  useEffect(t4, t5);
  const errorMessage = error ? (error as FieldError).message : undefined;
  const nickname = session?.user.nickname;
  const hasNickname = typeof nickname === "string" && nickname.length > 0;
  let t6;
  if ($[22] !== label) {
    t6 = Boolean(label) && <SubheadingH3 className="mt-0 mb-2">{label}</SubheadingH3>;
    $[22] = label;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const t7 = errorMessage === undefined ? "border-transparent" : "border-red-500";
  let t8;
  if ($[24] !== t7) {
    t8 = cn("flex flex-col gap-4 rounded-lg border-2 bg-gray-50 p-4", t7);
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== handleChangeUser || $[27] !== handleLogin || $[28] !== hasNickname || $[29] !== locale || $[30] !== nickname || $[31] !== session) {
    t9 = session?.user ? <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-sm text-gray-600">{loggedInAsText[locale]}:</p><p className="font-semibold text-gray-900">{session.user.name ?? session.user.email}{hasNickname && ` (${nickname})`}</p><p className="text-xs text-gray-500">{session.user.email}</p></div><button type="button" onClick={handleChangeUser} className="text-conveniat-green shrink-0 cursor-pointer text-sm font-medium hover:underline">{changeUserText[locale]}</button></div> : <div className="flex justify-center"><button type="button" onClick={handleLogin} className={cn("font-body relative flex cursor-pointer items-center justify-center rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:outline-none")}><span className="font-body text-center text-sm font-medium text-gray-500">{loginWithCeviDatabaseText[locale]}</span></button></div>;
    $[26] = handleChangeUser;
    $[27] = handleLogin;
    $[28] = hasNickname;
    $[29] = locale;
    $[30] = nickname;
    $[31] = session;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== t8 || $[34] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[33] = t8;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const t11 = required === true ? loginRequiredMessage[locale] : false;
  let t12;
  if ($[36] !== name || $[37] !== register || $[38] !== t11) {
    t12 = register(name, {
      required: t11
    });
    $[36] = name;
    $[37] = register;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t12) {
    t13 = <input type="hidden" {...t12} />;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== errorMessage) {
    t14 = Boolean(errorMessage) && <div className="mt-1 text-sm text-red-600">{errorMessage}</div>;
    $[42] = errorMessage;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t10 || $[45] !== t13 || $[46] !== t14 || $[47] !== t6) {
    t15 = <div className="mb-6">{t6}{t10}{t13}{t14}</div>;
    $[44] = t10;
    $[45] = t13;
    $[46] = t14;
    $[47] = t6;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  return t15;
};
function _temp() {
  return signOut({
    redirect: false
  });
}
function _temp2(resolve) {
  return setTimeout(resolve, PERSISTER_THROTTLE_MS + 200);
}
function _temp3(switchError) {
  console.warn("Switching the Cevi.DB login failed", switchError);
}